/**
 * Generates a random integer between the specified minimum and maximum values, inclusive.
 *
 * @param min - The minimum value (inclusive).
 * @param max - The maximum value (inclusive).
 * @returns A random integer between `min` and `max`.
 */
export function getRandomInt(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * The `q`-quantile (0..1) of `values`, linearly interpolated between the
 * two neighbouring samples. Sorts a copy, so the caller's array is left
 * alone. `values` must be non-empty.
 *
 * Interpolated rather than nearest-rank so the result moves continuously
 * with the data: a measurement summarised this way does not jump by a
 * whole sample when one is added.
 */
export function quantile(values: readonly number[], q: number): number {
    const sorted = [...values].sort((a, b) => a - b);
    const position = q * (sorted.length - 1);
    const lower = Math.floor(position);
    const upper = Math.ceil(position);
    if (lower === upper) {
        return sorted[lower];
    }
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

/** The median of `values`, which must be non-empty. */
export function median(values: readonly number[]): number {
    return quantile(values, 0.5);
}
